export type Tone = 'pink' | 'green' | 'amber' | 'slate' | 'rose' | 'blue';

export function formatDate(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function timeAgo(value?: string | null): string {
  if (!value) return '—';
  const diff = Date.now() - new Date(value).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(value);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const COMPANY_STATUS: Record<string, { label: string; tone: Tone; hint: string }> = {
  INVITED: { label: 'Invited', tone: 'amber', hint: 'Waiting for the company to open the workspace' },
  INTAKE: { label: 'Intake', tone: 'pink', hint: 'Company profile and documents in progress' },
  SUBMITTED: { label: 'Submitted', tone: 'blue', hint: 'Documents submitted — awaiting rating' },
  RATED: { label: 'Rated', tone: 'green', hint: 'Rating published — pay the fee, then update docs if needed' },
  REMEDIATING: { label: 'Rated', tone: 'green', hint: 'Rating published — update documents and resubmit for review' },
  REANALYSIS: { label: 'Re-analysis', tone: 'blue', hint: 'Updated documents submitted — rating is being refreshed' },
  READY: { label: 'Ready', tone: 'green', hint: 'Summary agreed — ready for funding partners' },
  OUTREACH: { label: 'Partner outreach', tone: 'green', hint: 'Shared with funding partners' },
  FUNDED: { label: 'Funded', tone: 'green', hint: 'Funding secured' },
};

export const DOC_STATUS: Record<string, { label: string; tone: Tone }> = {
  UPLOADED: { label: 'Received', tone: 'slate' },
  VERIFYING: { label: 'Processing', tone: 'blue' },
  VERIFIED: { label: 'Verified', tone: 'green' },
  NEEDS_REVIEW: { label: 'Needs replacement', tone: 'amber' },
  REJECTED: { label: 'Needs replacement', tone: 'rose' },
  WRONG_ENTITY: { label: 'Wrong entity', tone: 'rose' },
  EXPIRED: { label: 'Expired', tone: 'rose' },
  NOT_APPLICABLE: { label: 'Not applicable', tone: 'slate' },
  ANALYST_REVIEW: { label: 'Analyst review required', tone: 'amber' },
  SUPERSEDED: { label: 'Replaced', tone: 'slate' },
};

export const FACT_SOURCE: Record<string, { tone: Tone }> = {
  'Applicant provided': { tone: 'slate' },
  'Document verified': { tone: 'green' },
  'Public source': { tone: 'blue' },
  'Analyst confirmed': { tone: 'pink' },
  'Not verified': { tone: 'amber' },
};

export const SEVERITY: Record<string, { label: string; tone: Tone }> = {
  HIGH: { label: 'Major Concern', tone: 'rose' },
  MEDIUM: { label: 'Medium', tone: 'amber' },
  LOW: { label: 'Low', tone: 'slate' },
};

export const IMMUTABLE_KIND: Record<string, { label: string; hint: string }> = {
  HISTORIC: { label: 'Historic record', hint: 'Past events cannot be rewritten in the document' },
  STATEMENT: { label: 'Statement content', hint: 'Descriptive or attestational text that is not a fixable defect' },
  FACTUAL: { label: 'Established fact', hint: 'An accurate but adverse recorded fact' },
  OTHER: { label: 'Cannot be changed', hint: 'This finding cannot be fixed by editing or replacing the document' },
};

export const REMEDIATION_STATUS: Record<string, { label: string; tone: Tone }> = {
  OPEN: { label: 'Open', tone: 'pink' },
  SUBMITTED: { label: 'Addressed', tone: 'blue' },
  RESOLVED: { label: 'Resolved', tone: 'green' },
  SUPERSEDED: { label: 'Superseded', tone: 'slate' },
};

export const REPORT_STATUS: Record<string, { label: string; tone: Tone; hint: string }> = {
  DRAFT: { label: 'Draft', tone: 'slate', hint: 'Analyst is still working on this report' },
  PUBLISHED: { label: 'Published', tone: 'blue', hint: 'Issued by your Micus analyst' },
  APPROVED: { label: 'Shared-ready', tone: 'green', hint: 'Ready summary agreed — cleared for funding partners' },
  CHANGES_REQUESTED: { label: 'Under revision', tone: 'amber', hint: 'Your analyst is revising the report' },
};

export const SUBMISSION_STATUS: Record<string, { label: string; tone: Tone; waitingOn: 'partner' | 'you' | null }> = {
  SENT: { label: 'Sent', tone: 'blue', waitingOn: 'partner' },
  UNDER_REVIEW: { label: 'Under review', tone: 'blue', waitingOn: 'partner' },
  INFO_REQUESTED: { label: 'Info requested', tone: 'amber', waitingOn: 'you' },
  OFFERED: { label: 'Offer received', tone: 'pink', waitingOn: 'you' },
  ACCEPTED: { label: 'Offer accepted', tone: 'green', waitingOn: 'partner' },
  DISBURSED: { label: 'Disbursed', tone: 'green', waitingOn: null },
  DECLINED: { label: 'Declined', tone: 'rose', waitingOn: null },
  WITHDRAWN: { label: 'Withdrawn', tone: 'slate', waitingOn: null },
};

export const PAYMENT_STATUS: Record<string, { label: string; tone: Tone }> = {
  PENDING: { label: 'Due', tone: 'amber' },
  PAID: { label: 'Paid', tone: 'green' },
  FAILED: { label: 'Failed', tone: 'rose' },
  CANCELLED: { label: 'Cancelled', tone: 'slate' },
};

/** Amounts from the API are in minor units (paise / cents). */
export function formatMoney(minor: number, currency: string): string {
  try {
    return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en', { style: 'currency', currency }).format(minor / 100);
  } catch {
    return `${currency} ${(minor / 100).toFixed(2)}`;
  }
}

export const DOC_CATEGORY: Record<string, { label: string; hint: string }> = {
  FINANCIAL: { label: 'Financial statements & banking', hint: 'Audited accounts, tax returns, bank statements, debt' },
  LEGAL: { label: 'Legal & statutory', hint: 'Incorporation, registrations, ownership, licences, contracts' },
  KYC: { label: 'Promoter / partner KYC', hint: 'Identity, net worth and consent for the people behind the business' },
  BUSINESS: { label: 'Business profile', hint: 'What you do, customers, operations' },
  OTHER: { label: 'Other documents', hint: 'Additional documents requested for your country' },
};

export const TRACK_TONE: Record<string, Tone> = { CORPORATE: 'blue', PARTNERSHIP: 'amber', PROPRIETORSHIP: 'green' };

/** Prefer the unique Micus display name over the real/legal company name. */
export function companyDisplayName(c: { logicalName?: string | null; name: string }): string {
  return c.logicalName || c.name;
}

export function companyTitle(c: { logicalName?: string | null; name: string; workspaceLabel?: string | null }): string {
  const base = companyDisplayName(c);
  return c.workspaceLabel ? `${base} · ${c.workspaceLabel}` : base;
}

export function flagEmoji(countryCode?: string | null): string {
  if (!countryCode || countryCode.length !== 2) return '🏳';
  return String.fromCodePoint(...[...countryCode.toUpperCase()].map((ch) => 0x1f1a5 + ch.charCodeAt(0)));
}

/** Ordered company journey — drives the step tabs and progress rails. `readOnly` steps are owned by the Micus analyst. */
export const JOURNEY = [
  { key: 'INTAKE', label: 'Register', to: '/company', readOnly: false },
  { key: 'SUBMITTED', label: 'Submit docs', to: '/documents', readOnly: false },
  { key: 'RATED', label: 'Rated', to: '/rating', readOnly: true },
  { key: 'OUTREACH', label: 'Partners', to: '/partners', readOnly: true },
  { key: 'FUNDED', label: 'Funded', to: '/funded', readOnly: false },
] as const;

/** Customer Ready pack confirm lives at /ready but is not its own journey step. */
export function journeyPathForRoute(pathname: string): string {
  if (pathname === '/ready') return '/rating';
  return pathname;
}

export function journeyIndex(status?: string | null): number {
  switch (status) {
    case 'INVITED':
    case 'INTAKE':
      return 0;
    case 'SUBMITTED':
      return 1;
    case 'RATED':
    case 'REMEDIATING':
    case 'REANALYSIS':
      return 2;
    case 'READY':
    case 'OUTREACH':
      return 3;
    case 'FUNDED':
      return 4;
    default:
      return -1;
  }
}

export function gradeTone(score?: number | null): Tone {
  if (score == null) return 'slate';
  if (score >= 75) return 'green';
  if (score >= 55) return 'amber';
  return 'rose';
}
